import { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { getValidezTabla, getGrabarValidez, getEliminarValidez } from '../../../../service/AffariService';
import LoaderTransparent from '../../../../components/ui/LoaderTransparent';



const ValidezDialog = ({ visible, onCancel, codigo, onChangeValidezLista }) => {
    const [validez, setValidez] = useState([]);
    const [loading, setLoading] = useState(false);
    const [nuevaDescripcion, setNuevaDescripcion] = useState('');
    const [validezEditando, setValidezEditando] = useState(null);
    const [descripcionEditando, setDescripcionEditando] = useState('');
    const toast = useRef(null);

    useEffect(() => {
        if (!visible) return;
        setValidez([]);
        (async () => {
            try {
                setLoading(true);
                const resp = await getValidezTabla();
                const lista = Array.isArray(resp) ? resp : resp?.validezs || [];
                const soloActivas = lista.filter((item) => item?.activo === true);
                setValidez(soloActivas);
                if (onChangeValidezLista) {
                    onChangeValidezLista(soloActivas);
                }
            } catch (error) {
                console.error('Error obteniendo validez:', error);
            } finally {
                setLoading(false);
            }
        })();
    }, [visible]);

    const recargarValidez = async () => {
        setLoading(true);
        try {
            const resp = await getValidezTabla();
            const lista = Array.isArray(resp) ? resp : resp?.validezs || [];
            const soloActivas = lista.filter((item) => item?.activo === true);
            setValidez(soloActivas);
            if (onChangeValidezLista) {
                onChangeValidezLista(soloActivas);
            }
        } catch (error) {
            console.error('Error obteniendo validez:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCrearValidez = async () => {
        if (!nuevaDescripcion.trim()) return;
        setLoading(true);
        const descripcionLimpia = nuevaDescripcion.trim();

        try {
            await getGrabarValidez({ descripcion: descripcionLimpia });
            await recargarValidez();
            setNuevaDescripcion('');
            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Validez',
                    detail: 'Validez creada con éxito',
                    life: 3000,
                });
            }
        } catch (error) {
            console.error('Error creando validez:', error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Validez',
                    detail: 'No se pudo crear la validez',
                    life: 3000,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEliminarValidez = async (item) => {
        if (!item?.id) return;
        if (!window.confirm(`¿Eliminar la validez "${item.descripcion}"?`)) return;

        setLoading(true);

        try {
            await getEliminarValidez(item.id);
            await recargarValidez();
            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Validez',
                    detail: 'Validez eliminada con éxito',
                    life: 3000,
                });
            }
        } catch (error) {
            console.error('Error eliminando validez:', error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Validez',
                    detail: 'No se pudo eliminar la validez',
                    life: 3000,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEditarValidezStart = (item) => {
        if (!item?.id) return;
        setValidezEditando(item.id);
        setDescripcionEditando(item.descripcion || '');
    };

    const handleEditarValidezCancel = () => {
        setValidezEditando(null);
        setDescripcionEditando('');
    };

    const handleEditarValidezSave = () => {
        if (!validezEditando || !descripcionEditando.trim()) return;

        setLoading(true);
        const descripcionLimpia = descripcionEditando.trim();

        getGrabarValidez({ id: validezEditando, descripcion: descripcionLimpia })
            .then(recargarValidez)
            .then(() => {
                setValidezEditando(null);
                setDescripcionEditando('');
                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Validez',
                        detail: 'Validez actualizada con éxito',
                        life: 3000,
                    });
                }
            })
            .catch((error) => {
                console.error('Error actualizando validez:', error);
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Validez',
                        detail: 'No se pudo actualizar la validez',
                        life: 3000,
                    });
                }
            })
            .finally(() => setLoading(false));
    };

    return (
        <>

            <Toast ref={toast} position="top-right" baseZIndex={4000} appendTo={document.body} />
            <Dialog
            header={`Mantenedor de Validez`}
            visible={visible}
            className="p-fluid custom-dialog"
            modal
            onHide={onCancel}
            dismissableMask={true}
            >
                {loading && <LoaderTransparent coverParent overlayTint="transparent" />}
                <div className="card mb-3">
                    <div className="grid">
                        <div className="mt-3 flex flex-column gap-2">
                            <span className="font-bold">Crear nueva validez</span>
                            <div className="flex gap-2">
                                <InputText
                                    type="text"
                                    placeholder="Descripción"
                                    className="p-inputtext-sm"
                                    value={nuevaDescripcion}
                                    onChange={(e) => setNuevaDescripcion(e.target.value)}
                                />
                                <Button
                                    label="Agregar"
                                    className="p-button-success p-button-sm"
                                    icon="pi pi-plus"
                                    onClick={handleCrearValidez}
                                    disabled={!nuevaDescripcion.trim() || loading}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <DataTable
                    value={validez}
                    tableStyle={{ minWidth: "50rem" }}
                    size="small"
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    dataKey="id"
                    filterDisplay="row"
                    className="p-datatable-sm border-1 surface-border border-round"
                    globalFilterFields={["descripcion"]}
                    stripedRows
                    rowHover                              // resalta filas al hover
                    showGridlines                         // muestra líneas de cuadrícula
                    sortMode="multiple"                   // orden múltiple por columnas
                    removableSort                         // permite limpiar orden
                    resizableColumns                      // permitir redimensionar columnas
                    columnResizeMode="fit"                // ajuste de columnas al redimensionar
                    reorderableColumns                    // reordenar columnas arrastrando el header
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                    currentPageReportTemplate="{first} - {last} de {totalRecords}"
                    scrollable={true}
                    emptyMessage={<div className="p-4 hover:font-bold">No hay ningún registro que mostrar</div>}
                >
                    <Column
                        columnKey="acciones"
                        header="Acciones"
                        body={(rowData) => (
                            validezEditando === rowData.id ? (
                                <>
                                    <Button
                                        icon="pi pi-check"
                                        className="p-button-rounded p-button-text p-button-success p-button-sm"
                                        onClick={handleEditarValidezSave}
                                        disabled={loading || !descripcionEditando.trim()}
                                    />
                                    <Button
                                        icon="pi pi-times"
                                        className="p-button-rounded p-button-text p-button-secondary p-button-sm"
                                        onClick={handleEditarValidezCancel}
                                        disabled={loading}
                                    />
                                </>
                            ) : (
                                <>
                                    <Button
                                        icon="pi pi-file"
                                        className="p-button-rounded p-button-text p-button-secondary p-button-sm"
                                        onClick={() => handleEditarValidezStart(rowData)}
                                        disabled={loading}
                                    />
                                    <Button
                                        icon="pi pi-trash"
                                        className="p-button-rounded p-button-text p-button-secondary p-button-sm"
                                        onClick={() => handleEliminarValidez(rowData)}
                                        disabled={loading}
                                    />
                                </>
                            )
                        )}
                        style={{ width: '4.5rem' }}
                    />
                    <Column
                        columnKey="descripcion"
                        field="descripcion"
                        header="Descripción"
                        style={{ width: '12rem' }}
                        sortable
                        body={(rowData) => (
                            validezEditando === rowData.id ? (
                                <InputText
                                    value={descripcionEditando}
                                    onChange={(e) => setDescripcionEditando(e.target.value)}
                                    className="p-inputtext-sm w-full"
                                />
                            ) : (
                                rowData.descripcion
                            )
                        )}
                    />
                </DataTable>
            </Dialog>
        </>
    );
};

export default ValidezDialog;
